import React, { useContext, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import {
  Controller,
  FieldError,
  useFieldArray,
  useForm,
} from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import {
  businessMatchingOwnedBusinessEntityActiveBusinessEntityInfoApplicationPath,
  businessMatchingOwnedBusinessEntityBusinessEntityInfoApplicationEmailVerificationTokenPath,
  businessMatchingOwnedBusinessEntityDraftEditBusinessEntityInfoApplicationPath,
  validateBusinessMatchingOwnedBusinessEntityPath,
} from "../../../../../routes";
import { CroppableImageFileDropzone } from "../../../../shared/components/CroppableImageFileDropzone";
import { Box } from "../../../shared/components/ui/chakra";
import Button from "../../../shared/components/ui/Button";
import Background from "../../../shared/components/ui/Background";
import FormLabel from "../../../shared/components/ui/FormLabel";
import InputError from "../../../shared/components/ui/InputError";
import Radio from "../../../shared/components/ui/Radio";
import Checkbox from "../../../shared/components/ui/Checkbox";
import SectionTitle from "../../../shared/components/app/SectionTitle";
import ShadowCard from "../../../shared/components/app/ShadowCard";
import WarningMessage from "../../../shared/components/app/WarningMessage";
import AddIcon from "../../../shared/components/icons/AddIcon";
import MarkEmailUnreadIcon from "../../../shared/components/icons/MarkEmailUnreadIcon";
import SendIcon from "../../../shared/components/icons/SendIcon";
import useAutokana from "../../../shared/lib/useAutokana";
import useFlash from "../../../shared/lib/useFlash";
import useRequest from "../../../shared/lib/useRequest";
import InfoItem from "../../shared/components/InfoItem";
import InfoItemLeft from "../../shared/components/InfoItemLeft";
import InfoItemRight from "../../shared/components/InfoItemRight";
import OwnedBusinessEntityLayout from "../../shared/components/OwnedBusinessEntityLayout";
import fillAddressByPostalCode from "../../../../shared/lib/fillAddressByPostalCode";
import { handleValidateEmailResponse } from "../lib/handleValidateEmailResponse";
import { onCheckAllPrefecture } from "../lib/onCheckAllPrefecture";
import { openPreview } from "../../../../shared/lib/openPreview";
import { OwnedBusinessEntitiesEditProps } from "../lib/types";
import { useEditForm } from "../lib/useEditForm";
import { Step1FormData } from "../lib/useStep1Form";
import { Step2FormData } from "../lib/useStep2Form";
import BuildingTypeStructureTypeFieldForEdit from "./BuildingTypeStructureTypeFieldForEdit";
import Confirmation from "./Confirmation";
import DesigendArchitecturalFormForEdit from "./DesignedArchitecturalFormForEdit";
import {
  Address1Field,
  Address2Field,
  Address3Field,
  BranchNameField,
  CapitalField,
  ClientInfoDesiredBusinessServiceNoteField,
  ContractorInfoSupportedBusinessServiceNoteField,
  EmailField,
  EmployeesField,
  EstablishmentMonthField,
  EstablishmentYearField,
  FacebookUrlField,
  IndustryField,
  InstagramUrlField,
  IntroductionField,
  MainClientField,
  NameField,
  NameKanaField,
  NoteUrlField,
  PhoneNumberField,
  PinterestUrlField,
  PostalCodeField,
  PrefectureField,
  PresidentNameField,
  ShowUpToAddress1Field,
  ThreadsUrlField,
  TiktokUrlField,
  UseCaseField,
  WebsiteField,
  XUrlField,
  YoutubeUrlField,
} from "./FormFields";
import MultiSelect from "./MultiSelect";
import MultiSelectForOptions from "./MultiSelectForOptions";
import { validateFileSize } from "../../../../shared/lib/validateFileSize";
import {
  createUnifiedFile,
  UnifiedFile,
} from "../../../../shared/lib/UnifiedFile";
import StickyFooter from "./StickyFooter";
import {
  GroupedPrefectures,
  SharedArchitecturalSoftwares,
} from "../../../../shared/lib/types";
import BusinessMatchingHeader from "../../shared/components/BusinessMatchingHeader";
import { FlipperContext } from "../../../../shared/lib/FlipperContext";
import { businessTypes } from "../../../../shared/lib/businessTypes";

import {
  Container,
  Flex,
  Grid,
  Heading,
  HStack,
  ListItem,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  RadioGroup,
  Stack,
  Text,
  UnorderedList,
  useDisclosure,
} from "@chakra-ui/react";
import ErrorIcon from "../../../shared/components/icons/ErrorIcon";

const EditForm = ({
  flash,
  currentUser,
  prefectures,
  industries,
  ownedBusinessEntity,
  isApproved,
  applicationing,
  isEmailVerified,
  isRejected,
  architecturalSoftwares,
  businessFields,
  buildingTypes,
  structureTypes,
  groupedPrefectures,
  methods,
  draftContent,
  draftedAt,
  initialDesignedArchitecturalWorksImages = [],
}: OwnedBusinessEntitiesEditProps & {
  methods: ReturnType<typeof useEditForm>;
}) => {
  const [isAllPrefectureChecked, setIsAllPrefectureChecked] =
    useState<boolean>(false);
  const useCase = methods.watch("use_case");
  const {
    handleSubmit,
    control,
    setValue,
    setError,
    clearErrors,
    formState: { isDirty, isSubmitting, errors },
  } = methods;
  const { fields, append, remove } = useFieldArray({
    control,
    name: "contractor_info_attributes.architectural_design_works_attributes",
  });
  const [
    isDirtyDesignedArchitecturalWorksImages,
    setIsDirtyDesignedArchitecturalWorksImages,
  ] = useState(false);
  const [
    designedArchitecturalWorksImages,
    setDesignedArchitecturalWorksImages,
  ] = useState<UnifiedFile[][]>(initialDesignedArchitecturalWorksImages);

  const nameAutoKana = useAutokana("#name", "#name_kana");
  const request = useRequest();
  const showFlash = useFlash();
  const resendingVerificationMailFormMethods = useForm();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    handleSubmit: handleFinalFormSubmit,
    formState: { isSubmitting: isFinalFormSubmitting },
  } = useForm();
  const architecturalDesignWorksRef = useRef<HTMLDivElement>(null);

  const architecturalDesignWorksAttributesError =
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    (errors.architectural_design_works_attributes as FieldError | undefined)
      ?.message ?? "";

  const createRequestData = async (data: Step1FormData & Step2FormData) => {
    const { uploadedFile: uploadedLogoImage, error: logoImageError } =
      await createUnifiedFile(data.logo_image).upload();
    if (logoImageError != null) {
      showFlash({ error: logoImageError });
      return { isError: true };
    }

    const designedArchitecturalWorksImagesUploadResults = await Promise.all(
      designedArchitecturalWorksImages.map((files) =>
        Promise.all(files.map((file) => file.upload())),
      ),
    );

    if (
      designedArchitecturalWorksImagesUploadResults.some((result) =>
        result.some((r) => r.error != null),
      )
    ) {
      showFlash({ error: "ファイルのアップロードに失敗しました" });
      return { isError: true };
    }

    const architecturalDesignWorksImages =
      designedArchitecturalWorksImagesUploadResults.map((result) =>
        result.map((r) => r.uploadedFile!.signedId),
      );

    let contractor_info_attributes: (typeof data)["contractor_info_attributes"] =
      null;

    if (
      (useCase === "both" || useCase === "contractor") &&
      data.contractor_info_attributes
    ) {
      contractor_info_attributes = {
        ...data.contractor_info_attributes,
        architectural_design_works_attributes:
          data.contractor_info_attributes.architectural_design_works_attributes.map(
            (work, index) => {
              return {
                ...work,
                images: architecturalDesignWorksImages[index] ?? [],
              };
            },
          ) || [],
      };
    } else if (ownedBusinessEntity.contractor_info_attributes) {
      contractor_info_attributes = {
        ...ownedBusinessEntity.contractor_info_attributes,
        architectural_design_works_attributes:
          ownedBusinessEntity.contractor_info_attributes.architectural_design_works_attributes.map(
            (work) => ({
              ...work,
              images: work.images.map((image) => image.signed_id),
            }),
          ) ?? [],
      };
    }

    const client_info_attributes =
      useCase === "both" || useCase === "client"
        ? data.client_info_attributes
        : ownedBusinessEntity.client_info_attributes;

    return {
      requestData: {
        ...data,
        logo_image: uploadedLogoImage.signedId,
        contractor_info_attributes,
        client_info_attributes,
      },
    };
  };

  const validateEmail = async (data: Step1FormData) => {
    const res = await request(
      validateBusinessMatchingOwnedBusinessEntityPath(ownedBusinessEntity.code),
      "POST",
      { business_entity_info_application: { email: data.email } },
    );
    return await handleValidateEmailResponse({
      response: res,
      methods: { setError, clearErrors },
    });
  };

  const onConfirm = async (data: Step1FormData & Step2FormData) => {
    if (!(await validateEmail(data))) {
      return;
    }

    await navigate("#confirm");
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const onSubmit = async () => {
    const data = methods.getValues();
    if (!(await validateEmail(data))) {
      return;
    }

    const { isError, requestData } = await createRequestData(data);

    if (isError != null) return;

    const { establishment_year, establishment_month, ...rest } = requestData;

    const res = await request(
      businessMatchingOwnedBusinessEntityActiveBusinessEntityInfoApplicationPath(
        ownedBusinessEntity.code,
      ),
      "POST",
      {
        business_entity_info_application: {
          ...rest,
          establishment_year_month: `${establishment_year}/${establishment_month}`,
        },
      },
    );

    if (res.ok) {
      // #previewを除去するためpathnameを代入
      window.location.href = window.location.pathname;
    }
  };

  const draft = async () => {
    const { isError, requestData } = await createRequestData(
      methods.getValues(),
    );
    if (isError != null) return;

    const res = await request(
      businessMatchingOwnedBusinessEntityDraftEditBusinessEntityInfoApplicationPath(
        ownedBusinessEntity.code,
      ),
      "POST",
      {
        draft_edit_business_entity_info_application: requestData,
      },
    );

    if (res.ok) {
      showFlash({
        success: "下書き保存しました",
      });
    }
  };

  const resendVerificationMail = async () => {
    const res = await request(
      businessMatchingOwnedBusinessEntityBusinessEntityInfoApplicationEmailVerificationTokenPath(
        ownedBusinessEntity.code,
      ),
      "PUT",
    );

    if (res.ok) {
      showFlash({
        success: <>認証メールを送信しました</>,
      });
    } else {
      showFlash({
        error: (
          <>
            認証メールの送信に失敗しました
            <br />
            ページをリロードしてください
          </>
        ),
      });
    }
  };

  const midiumFormMaxW = "320px";
  const shortFormMaxW = "240px";
  const snsFormLabelMinW = { base: "initial", md: "136px" };
  const snsFlexProps = {
    justifyContent: "space-between",
    align: { base: "initial", md: "center" },
    direction: { base: "column", md: "row" },
  } as const;

  const restoreDraft = () => {
    if (draftContent) {
      methods.reset(draftContent, { keepDefaultValues: true });
      setDesignedArchitecturalWorksImages(
        draftContent.contractor_info_attributes?.architectural_design_works_attributes.map(
          (architecturalDesignWork) =>
            architecturalDesignWork.images.map((image) =>
              createUnifiedFile(image),
            ),
        ) ?? [[]],
      );
    }
  };

  const deleteDraft = async () => {
    const res = await request(
      businessMatchingOwnedBusinessEntityDraftEditBusinessEntityInfoApplicationPath(
        ownedBusinessEntity.code,
      ),
      "DELETE",
    );

    if (res.ok) {
      showFlash({ success: "下書きを削除しました" });
    }
  };

  const gridProps = {
    rowGap: 6,
    columnGap: 8,
    gridTemplateColumns: { base: "1fr", lg: "minmax(0, 200px) 1fr" },
  };

  const flipper = useContext(FlipperContext);

  return (
    <Box position="relative">
      <Box display={location.hash === "#confirm" ? "none" : "block"}>
        <OwnedBusinessEntityLayout
          flash={flash}
          currentUser={currentUser}
          currentBusinessEntity={{
            ...ownedBusinessEntity,
            is_approved: isApproved,
          }}
        >
          <Helmet>
            <style type="text/css">{`
          html {
            scroll-behavior: smooth;
          }
        `}</style>
          </Helmet>
          <Box
            as="form"
            mb={32}
            onSubmit={handleSubmit(onConfirm, (error) => {
              console.error(error);
              if (
                architecturalDesignWorksAttributesError !== "" &&
                architecturalDesignWorksRef.current
              ) {
                architecturalDesignWorksRef.current.scrollIntoView({
                  block: "center",
                  behavior: "instant",
                });
              }
            })}
          >
            <Box background="#FFF" py={9} px={10} borderRadius="2px" id="info">
              <Heading as="h2" size="lg">
                事業者情報
              </Heading>
              {isEmailVerified === false ? (
                <>
                  <Flex
                    mt={4}
                    p={3}
                    gap={3}
                    color="#9B2C2C"
                    bgColor="#FFF5F5"
                    borderRadius="2px"
                    align="center"
                    direction={{ base: "column", md: "row" }}
                    justifyContent="space-between"
                  >
                    <HStack gap={3}>
                      <MarkEmailUnreadIcon
                        boxSize={8}
                        color="text.caution.dark"
                      />
                      <Text fontSize="sm">
                        申請いただいたメールアドレスにメール認証に関するメールを送信しました。
                        <br />
                        メールアドレスの認証後、掲載の審査を行います。
                      </Text>
                    </HStack>
                    <Button
                      pl={3}
                      pr={4}
                      py={2.5}
                      flexShrink={0}
                      w={{ base: "full", md: "fit-content" }}
                      onClick={resendingVerificationMailFormMethods.handleSubmit(
                        resendVerificationMail,
                      )}
                      isDisabled={
                        resendingVerificationMailFormMethods.formState
                          .isSubmitting
                      }
                    >
                      <HStack gap={1}>
                        <SendIcon boxSize="18px" />
                        <Box as="span" pt={0.5} fontSize="sm">
                          認証メール再送信
                        </Box>
                      </HStack>
                    </Button>
                  </Flex>
                </>
              ) : applicationing ? (
                <WarningMessage mt={4}>
                  現在、審査中のため修正を行うことができません。
                  <br />
                  修正を行う場合はページ下部の「審査依頼を取り下げる」をクリックして修正を行ってください。
                </WarningMessage>
              ) : (
                <Box>
                  <Box
                    p={3}
                    color="#9B2C2C"
                    bgColor="#FFF5F5"
                    borderRadius="2px"
                    mt={4}
                  >
                    <Stack>
                      <HStack alignItems="flex-start">
                        <ErrorIcon />
                        <Text fontSize="sm">
                          掲載する事業者情報を登録してください。
                          <br />
                          事業者情報の審査依頼後、弊社で内容の審査を行います。
                        </Text>
                      </HStack>
                      {flipper.disable_draft_feature && draftContent && (
                        <HStack alignItems="flex-start">
                          <ErrorIcon />
                          <Text fontSize="sm">
                            {draftedAt}に下書きした事業者情報を表示しています。
                          </Text>
                        </HStack>
                      )}
                    </Stack>
                  </Box>
                </Box>
              )}
              <Box mt={8}>
                支店でご利用の場合は、支店の住所、電話番号などを登録してください。
              </Box>
              <Grid mt={8} {...gridProps}>
                <InfoItem>
                  <InfoItemLeft>
                    <FormLabel htmlFor="name" required>
                      事業者区分
                    </FormLabel>
                  </InfoItemLeft>
                  <InfoItemRight>
                    <Controller
                      name="business_type"
                      control={control}
                      render={({ field, fieldState }) => {
                        const { ref, ...rest } = field;
                        return (
                          <>
                            <RadioGroup {...rest}>
                              <Stack>
                                {businessTypes.map((businessType) => (
                                  <Radio
                                    value={businessType.value}
                                    key={businessType.value}
                                    ref={ref}
                                    isDisabled={field.disabled}
                                  >
                                    {businessType.label}
                                  </Radio>
                                ))}
                              </Stack>
                            </RadioGroup>
                            {fieldState.error?.message !== undefined && (
                              <InputError>
                                {fieldState.error.message}
                              </InputError>
                            )}
                          </>
                        );
                      }}
                    />
                  </InfoItemRight>
                </InfoItem>
                <InfoItem>
                  <InfoItemLeft>
                    <FormLabel htmlFor="name" required>
                      事業者名
                    </FormLabel>
                  </InfoItemLeft>
                  <InfoItemRight>
                    <Controller
                      name="name"
                      control={control}
                      render={({ field, fieldState }) => (
                        <NameField
                          field={field}
                          fieldState={fieldState}
                          onInput={() => {
                            setValue(
                              "name_kana",
                              nameAutoKana?.getFurigana() ?? "",
                            );
                          }}
                          isNoLabel
                        />
                      )}
                    />
                  </InfoItemRight>
                </InfoItem>
                <InfoItem>
                  <InfoItemLeft>
                    <FormLabel htmlFor="name_kana">事業者名かな</FormLabel>
                  </InfoItemLeft>
                  <InfoItemRight>
                    <Controller
                      name="name_kana"
                      control={control}
                      render={({ field, fieldState }) => (
                        <NameKanaField
                          field={field}
                          fieldState={fieldState}
                          isNoLabel
                        />
                      )}
                    />
                  </InfoItemRight>
                </InfoItem>
                <InfoItem>
                  <InfoItemLeft>
                    <FormLabel htmlFor="branch_name">支店名</FormLabel>
                  </InfoItemLeft>
                  <InfoItemRight>
                    <Controller
                      name="branch_name"
                      control={control}
                      render={({ field, fieldState }) => (
                        <BranchNameField
                          field={field}
                          fieldState={fieldState}
                          isNoLabel
                        />
                      )}
                    />
                  </InfoItemRight>
                </InfoItem>
                <Grid gridColumn="1/-1" gridTemplateColumns="subgrid">
                  <InfoItem>
                    <InfoItemLeft>
                      <FormLabel required>事業者所在地</FormLabel>
                    </InfoItemLeft>
                  </InfoItem>
                  <Grid
                    mt={{ base: 2, md: 3 }}
                    gridColumn="1/-1"
                    pl={5}
                    borderLeft="1px solid"
                    borderColor="primary"
                    {...gridProps}
                    gridTemplateColumns="subgrid"
                  >
                    <InfoItem>
                      <InfoItemLeft>
                        <FormLabel htmlFor="postal_code" required>
                          郵便番号
                        </FormLabel>
                      </InfoItemLeft>
                      <InfoItemRight>
                        <Box maxW={shortFormMaxW}>
                          <Controller
                            name="postal_code"
                            control={control}
                            render={({ field, fieldState }) => (
                              <PostalCodeField
                                field={field}
                                fieldState={fieldState}
                                isNoLabel
                                onChange={async (e) => {
                                  field.onChange(e);
                                  await fillAddressByPostalCode({
                                    prefectures,
                                    postalCode: e.target.value,
                                    form: methods,
                                  });
                                }}
                              />
                            )}
                          />
                        </Box>
                      </InfoItemRight>
                    </InfoItem>
                    <InfoItem>
                      <InfoItemLeft>
                        <FormLabel htmlFor="prefecture_id" required>
                          都道府県
                        </FormLabel>
                      </InfoItemLeft>
                      <InfoItemRight>
                        <Box maxW={shortFormMaxW}>
                          <Controller
                            name="prefecture_id"
                            control={control}
                            render={({ field, fieldState }) => (
                              <PrefectureField
                                field={field}
                                fieldState={fieldState}
                                isNoLabel
                                prefectures={prefectures}
                              />
                            )}
                          />
                        </Box>
                      </InfoItemRight>
                    </InfoItem>
                    <InfoItem>
                      <InfoItemLeft>
                        <FormLabel htmlFor="address1" required>
                          市区町村
                        </FormLabel>
                      </InfoItemLeft>
                      <InfoItemRight>
                        <Controller
                          name="address1"
                          control={control}
                          render={({ field, fieldState }) => (
                            <Address1Field
                              field={field}
                              fieldState={fieldState}
                              isNoLabel
                            />
                          )}
                        />
                      </InfoItemRight>
                    </InfoItem>
                    <InfoItem>
                      <InfoItemLeft>
                        <FormLabel htmlFor="address2" required>
                          番地
                        </FormLabel>
                      </InfoItemLeft>
                      <InfoItemRight>
                        <Controller
                          name="address2"
                          control={control}
                          render={({ field, fieldState }) => (
                            <Address2Field
                              field={field}
                              fieldState={fieldState}
                              isNoLabel
                            />
                          )}
                        />
                      </InfoItemRight>
                    </InfoItem>
                    <InfoItem>
                      <InfoItemLeft>
                        <FormLabel htmlFor="address3">建物名</FormLabel>
                      </InfoItemLeft>
                      <InfoItemRight>
                        <Controller
                          name="address3"
                          control={control}
                          render={({ field, fieldState }) => (
                            <Address3Field
                              field={field}
                              fieldState={fieldState}
                              isNoLabel
                            />
                          )}
                        />
                      </InfoItemRight>
                    </InfoItem>
                    <InfoItem>
                      <InfoItemLeft />
                      <InfoItemRight>
                        <Controller
                          name="display_address_restricted"
                          control={control}
                          render={({ field }) => (
                            <ShowUpToAddress1Field field={field} />
                          )}
                        />
                      </InfoItemRight>
                    </InfoItem>
                  </Grid>
                </Grid>
                <InfoItem>
                  <InfoItemLeft>
                    <FormLabel htmlFor="phone_number" required>
                      電話番号
                    </FormLabel>
                  </InfoItemLeft>
                  <InfoItemRight>
                    <Box maxW={midiumFormMaxW}>
                      <Controller
                        name="phone_number"
                        control={control}
                        render={({ field, fieldState }) => (
                          <PhoneNumberField
                            field={field}
                            fieldState={fieldState}
                            isNoLabel
                          />
                        )}
                      />
                    </Box>
                  </InfoItemRight>
                </InfoItem>
                <InfoItem>
                  <InfoItemLeft>
                    <FormLabel htmlFor="email" required>
                      受発注の通知を受け取る
                      <br />
                      メールアドレス
                    </FormLabel>
                  </InfoItemLeft>
                  <InfoItemRight>
                    <Box>
                      <Controller
                        name="email"
                        control={control}
                        render={({ field, fieldState }) => (
                          <EmailField
                            field={field}
                            fieldState={fieldState}
                            isNoLabel
                          />
                        )}
                      />
                    </Box>
                  </InfoItemRight>
                </InfoItem>
                <InfoItem>
                  <InfoItemLeft>
                    <FormLabel htmlFor="industry_id" required>
                      主業種
                    </FormLabel>
                  </InfoItemLeft>
                  <InfoItemRight>
                    <Box maxW={shortFormMaxW}>
                      <Controller
                        name="industry_id"
                        control={control}
                        render={({ field, fieldState }) => (
                          <IndustryField
                            field={field}
                            fieldState={fieldState}
                            isNoLabel
                            industries={industries}
                          />
                        )}
                      />
                    </Box>
                  </InfoItemRight>
                </InfoItem>
                <InfoItem>
                  <InfoItemLeft>
                    <FormLabel htmlFor="number_of_employees" required>
                      従業員数
                    </FormLabel>
                  </InfoItemLeft>
                  <InfoItemRight>
                    <Box>
                      <Controller
                        name="number_of_employees"
                        control={control}
                        render={({ field, fieldState }) => (
                          <EmployeesField
                            field={field}
                            fieldState={fieldState}
                            isNoLabel
                            inputProps={{ maxW: shortFormMaxW }}
                          />
                        )}
                      />
                    </Box>
                  </InfoItemRight>
                </InfoItem>
                <InfoItem>
                  <InfoItemLeft>
                    <FormLabel required>設立年月</FormLabel>
                  </InfoItemLeft>
                  <InfoItemRight>
                    <HStack gap={4}>
                      <HStack gap={2} w="full">
                        <Box maxW={shortFormMaxW} w="full">
                          <Controller
                            name="establishment_year"
                            control={control}
                            render={({ field, fieldState }) => (
                              <EstablishmentYearField
                                field={field}
                                fieldState={fieldState}
                              />
                            )}
                          />
                        </Box>
                        <Text as="span">年</Text>
                      </HStack>
                      <HStack gap={2} w="full">
                        <Box maxW={shortFormMaxW} w="full">
                          <Controller
                            name="establishment_month"
                            control={control}
                            render={({ field, fieldState }) => (
                              <EstablishmentMonthField
                                field={field}
                                fieldState={fieldState}
                              />
                            )}
                          />
                        </Box>
                        <Text as="span">月</Text>
                      </HStack>
                    </HStack>
                  </InfoItemRight>
                </InfoItem>
                <InfoItem>
                  <InfoItemLeft>
                    <FormLabel htmlFor="capital_tem_thousand_yen" required>
                      資本金
                    </FormLabel>
                  </InfoItemLeft>
                  <InfoItemRight>
                    <Box>
                      <Controller
                        name="capital_ten_thousand_yen"
                        control={control}
                        render={({ field, fieldState }) => (
                          <CapitalField
                            field={field}
                            fieldState={fieldState}
                            inputProps={{ maxW: shortFormMaxW }}
                          />
                        )}
                      />
                    </Box>
                  </InfoItemRight>
                </InfoItem>
                <InfoItem>
                  <InfoItemLeft>
                    <FormLabel>事業者ロゴ</FormLabel>
                  </InfoItemLeft>
                  <InfoItemRight>
                    <Controller
                      control={control}
                      name="logo_image"
                      render={({
                        field: { ref, disabled, onChange, value },
                        fieldState: { error },
                      }) => (
                        <CroppableImageFileDropzone
                          onChange={(file) => {
                            if (
                              file &&
                              !validateFileSize({
                                file,
                                onError: (error) => showFlash({ error }),
                              })
                            ) {
                              return;
                            }
                            onChange(file);
                          }}
                          value={createUnifiedFile(value)}
                          label="事業者ロゴ"
                          inputRef={ref}
                          error={error?.message}
                          deleteButtonLabel="登録している事業者ロゴを削除する"
                          previewBoxProps={{
                            maxW: { base: "full", sm: "50%" },
                          }}
                          showDeleteButton={!disabled}
                          customMessage={({ sharedMessage }) => (
                            <UnorderedList>
                              <ListItem>
                                ロゴを掲載することで閲覧効果が高まります。
                              </ListItem>
                              <ListItem>
                                事業者ロゴがない場合は設計実績の建物写真などを登録してください。
                              </ListItem>
                              <ListItem>{sharedMessage}</ListItem>
                            </UnorderedList>
                          )}
                        />
                      )}
                    />
                  </InfoItemRight>
                </InfoItem>
                <InfoItem>
                  <InfoItemLeft>
                    <FormLabel htmlFor="president_name" required>
                      代表者氏名
                    </FormLabel>
                  </InfoItemLeft>
                  <InfoItemRight>
                    <Controller
                      name="president_name"
                      control={control}
                      render={({ field, fieldState }) => (
                        <PresidentNameField
                          field={field}
                          fieldState={fieldState}
                          isNoLabel
                        />
                      )}
                    />
                  </InfoItemRight>
                </InfoItem>
                <InfoItem>
                  <InfoItemLeft>
                    <FormLabel htmlFor="website_url">Webサイト</FormLabel>
                  </InfoItemLeft>
                  <InfoItemRight>
                    <Controller
                      name="website_url"
                      control={control}
                      render={({ field, fieldState }) => (
                        <WebsiteField
                          field={field}
                          fieldState={fieldState}
                          isNoLabel
                        />
                      )}
                    />
                  </InfoItemRight>
                </InfoItem>
                <InfoItem>
                  <InfoItemLeft>
                    <FormLabel fontSize={{ base: "initial", md: "lg" }}>
                      SNS
                    </FormLabel>
                  </InfoItemLeft>
                  <InfoItemRight mt={{ base: 2, md: 0 }}>
                    <Stack gap={6}>
                      <Flex {...snsFlexProps}>
                        <FormLabel minW={snsFormLabelMinW}>Facebook</FormLabel>
                        <Box flex={1}>
                          <Controller
                            name="facebook_url"
                            control={control}
                            render={({ field, fieldState }) => (
                              <FacebookUrlField
                                field={field}
                                fieldState={fieldState}
                                isNoLabel
                              />
                            )}
                          />
                        </Box>
                      </Flex>
                      <Flex {...snsFlexProps}>
                        <FormLabel minW={snsFormLabelMinW}>Instagram</FormLabel>
                        <Box flex={1}>
                          <Controller
                            name="instagram_url"
                            control={control}
                            render={({ field, fieldState }) => (
                              <InstagramUrlField
                                field={field}
                                fieldState={fieldState}
                                isNoLabel
                              />
                            )}
                          />
                        </Box>
                      </Flex>
                      <Flex {...snsFlexProps}>
                        <FormLabel minW={snsFormLabelMinW}>note</FormLabel>
                        <Box flex={1}>
                          <Controller
                            name="note_url"
                            control={control}
                            render={({ field, fieldState }) => (
                              <NoteUrlField
                                field={field}
                                fieldState={fieldState}
                                isNoLabel
                              />
                            )}
                          />
                        </Box>
                      </Flex>
                      <Flex {...snsFlexProps}>
                        <FormLabel minW={snsFormLabelMinW}>Pinterest</FormLabel>
                        <Box flex={1}>
                          <Controller
                            name="pinterest_url"
                            control={control}
                            render={({ field, fieldState }) => (
                              <PinterestUrlField
                                field={field}
                                fieldState={fieldState}
                                isNoLabel
                              />
                            )}
                          />
                        </Box>
                      </Flex>
                      <Flex {...snsFlexProps}>
                        <FormLabel minW={snsFormLabelMinW}>Threads</FormLabel>
                        <Box flex={1}>
                          <Controller
                            name="threads_url"
                            control={control}
                            render={({ field, fieldState }) => (
                              <ThreadsUrlField
                                field={field}
                                fieldState={fieldState}
                                isNoLabel
                              />
                            )}
                          />
                        </Box>
                      </Flex>
                      <Flex {...snsFlexProps}>
                        <FormLabel minW={snsFormLabelMinW}>TikTok</FormLabel>
                        <Box flex={1}>
                          <Controller
                            name="tiktok_url"
                            control={control}
                            render={({ field, fieldState }) => (
                              <TiktokUrlField
                                field={field}
                                fieldState={fieldState}
                                isNoLabel
                              />
                            )}
                          />
                        </Box>
                      </Flex>
                      <Flex {...snsFlexProps}>
                        <FormLabel minW={snsFormLabelMinW}>
                          X（旧Twitter）
                        </FormLabel>
                        <Box flex={1}>
                          <Controller
                            name="x_url"
                            control={control}
                            render={({ field, fieldState }) => (
                              <XUrlField
                                field={field}
                                fieldState={fieldState}
                                isNoLabel
                              />
                            )}
                          />
                        </Box>
                      </Flex>
                      <Flex {...snsFlexProps}>
                        <FormLabel minW={snsFormLabelMinW}>Youtube</FormLabel>
                        <Box flex={1}>
                          <Controller
                            name="youtube_url"
                            control={control}
                            render={({ field, fieldState }) => (
                              <YoutubeUrlField
                                field={field}
                                fieldState={fieldState}
                                isNoLabel
                              />
                            )}
                          />
                        </Box>
                      </Flex>
                    </Stack>
                  </InfoItemRight>
                </InfoItem>
              </Grid>
            </Box>

            <Box background="#FFF" p={8} borderRadius="2px" mt={10} id="detail">
              <Heading as="h2" size="lg">
                事業者情報詳細
              </Heading>
              <Box as="section" mt={14}>
                <SectionTitle>受発注の利用用途</SectionTitle>
                <Grid mt={8} {...gridProps}>
                  <InfoItem>
                    <InfoItemLeft>
                      <FormLabel required>利用用途</FormLabel>
                    </InfoItemLeft>
                    <InfoItemRight>
                      <Controller
                        name="use_case"
                        control={control}
                        render={({ field, fieldState }) => (
                          <UseCaseField field={field} fieldState={fieldState} />
                        )}
                      />
                    </InfoItemRight>
                  </InfoItem>
                </Grid>
              </Box>
              <Box as="section" mt={14}>
                <SectionTitle>事業者プロフィール</SectionTitle>
                <Grid mt={8} {...gridProps}>
                  <InfoItem>
                    <InfoItemLeft>
                      <FormLabel required>事業者紹介</FormLabel>
                    </InfoItemLeft>
                    <InfoItemRight>
                      <Controller
                        control={control}
                        name="introduction"
                        render={({ field, fieldState }) => (
                          <IntroductionField
                            field={field}
                            fieldState={fieldState}
                            isNoLabel
                          />
                        )}
                      />
                    </InfoItemRight>
                  </InfoItem>
                  <InfoItem>
                    <InfoItemLeft>
                      <FormLabel>利用している建築系ソフトウェア</FormLabel>
                    </InfoItemLeft>
                    <InfoItemRight>
                      <Stack gap={4}>
                        <Controller
                          control={control}
                          name="used_architectural_software_ids"
                          render={({
                            field: { disabled, value, onChange, ...field },
                            fieldState: { error },
                          }) => (
                            <Box>
                              <MultiSelect
                                options={architecturalSoftwares.map(
                                  ({ category, items }) => ({
                                    label: category,
                                    options: items.map((item) => ({
                                      label: item.name,
                                      sublabel:
                                        item.name === "その他"
                                          ? category
                                          : undefined,
                                      value: item.id.toString(),
                                    })),
                                  }),
                                )}
                                {...field}
                                isDisabled={disabled}
                                value={value.map((v) => {
                                  const software = architecturalSoftwereById({
                                    id: v,
                                    architecturalSoftwares,
                                  });
                                  return {
                                    label: software?.name ?? "",
                                    sublabel:
                                      software?.name === "その他"
                                        ? softwareCategoryBySoftwareId({
                                            id: v,
                                            architecturalSoftwares,
                                          })
                                        : undefined,
                                    value: software?.id.toString() ?? "",
                                  };
                                })}
                                onChange={(selectedOptions) => {
                                  onChange(
                                    selectedOptions.map(
                                      (option) => option.value,
                                    ),
                                  );
                                }}
                              />
                              {error && (
                                <InputError>{error.message}</InputError>
                              )}
                            </Box>
                          )}
                        />
                      </Stack>
                    </InfoItemRight>
                  </InfoItem>
                  <InfoItem>
                    <InfoItemLeft>
                      <FormLabel>主な取引先</FormLabel>
                    </InfoItemLeft>
                    <InfoItemRight>
                      <Controller
                        control={control}
                        name="main_client"
                        render={({ field, fieldState }) => (
                          <MainClientField
                            field={field}
                            fieldState={fieldState}
                            isNoLabel
                          />
                        )}
                      />
                    </InfoItemRight>
                  </InfoItem>
                </Grid>
              </Box>
              {(useCase === "contractor" || useCase === "both") && (
                <Box mt={14}>
                  <SectionTitle>受注したい業務</SectionTitle>
                  <Grid mt={8} {...gridProps}>
                    <InfoItem>
                      <InfoItemLeft>
                        <FormLabel required>受注したい業種</FormLabel>
                      </InfoItemLeft>
                      <InfoItemRight>
                        <Controller
                          control={control}
                          name="contractor_info_attributes.desired_industry_ids"
                          render={({
                            field: { disabled, value, onChange, ...field },
                            fieldState: { error },
                          }) => (
                            <Box>
                              <MultiSelectForOptions
                                options={industries}
                                value={value}
                                onChange={onChange}
                                isDisabled={disabled}
                                {...field}
                              />
                              {error && (
                                <InputError>{error?.message}</InputError>
                              )}
                            </Box>
                          )}
                        />
                      </InfoItemRight>
                    </InfoItem>
                    <InfoItem>
                      <InfoItemLeft>
                        <FormLabel required>対応できる分野</FormLabel>
                      </InfoItemLeft>
                      <InfoItemRight>
                        <Controller
                          control={control}
                          name="contractor_info_attributes.supported_business_field_ids"
                          render={({
                            field: { disabled, value, onChange, ...field },
                            fieldState: { error },
                          }) => (
                            <Box>
                              <MultiSelectForOptions
                                options={businessFields}
                                value={value}
                                onChange={onChange}
                                isDisabled={disabled}
                                {...field}
                              />
                              {error && (
                                <InputError>{error?.message}</InputError>
                              )}
                            </Box>
                          )}
                        />
                      </InfoItemRight>
                    </InfoItem>
                    <InfoItem>
                      <InfoItemLeft>
                        <FormLabel required>対応できる業務内容</FormLabel>
                      </InfoItemLeft>
                      <InfoItemRight>
                        <Controller
                          control={control}
                          name="contractor_info_attributes.supported_business_service_note"
                          render={({ field, fieldState }) => (
                            <ContractorInfoSupportedBusinessServiceNoteField
                              field={field}
                              fieldState={fieldState}
                              isNoLabel
                            />
                          )}
                        />
                      </InfoItemRight>
                    </InfoItem>
                    <InfoItem>
                      <InfoItemLeft>
                        <FormLabel required>対応できる建物種別</FormLabel>
                      </InfoItemLeft>
                      <InfoItemRight>
                        <Controller
                          control={control}
                          name="contractor_info_attributes.contractor_info_supported_building_types_attributes"
                          render={({
                            field: { value, onChange, ref, disabled },
                            fieldState: { error },
                          }) => (
                            <Box ref={ref} tabIndex={-1}>
                              <BuildingTypeStructureTypeFieldForEdit
                                value={value}
                                onChange={onChange}
                                errors={
                                  error as
                                    | {
                                        building_type_id?: FieldError;
                                        structure_type_ids?: FieldError;
                                      }[]
                                    | undefined
                                }
                                labelPrefix="対応できる"
                                buildingTypes={buildingTypes}
                                structureTypes={structureTypes}
                                isDisabled={disabled}
                              />
                            </Box>
                          )}
                        />
                      </InfoItemRight>
                    </InfoItem>
                    <InfoItem>
                      <InfoItemLeft>
                        <FormLabel required>対応可能エリア</FormLabel>
                      </InfoItemLeft>
                      <InfoItemRight>
                        <Controller
                          control={control}
                          name="contractor_info_attributes.supported_prefecture_ids"
                          render={({
                            field: { ref, disabled, value, onChange, ...field },
                            fieldState: { error },
                          }) => (
                            <Box ref={ref} tabIndex={-1}>
                              <MultiSelect
                                inlineOption
                                options={groupedPrefectures.map(
                                  ({ region, prefectures }) => ({
                                    label: region,
                                    options: prefectures.map((prefecture) => ({
                                      label: prefecture.name,
                                      value: prefecture.id.toString(),
                                    })),
                                  }),
                                )}
                                isDisabled={disabled}
                                value={value.map((v) => {
                                  const prefecture = prefectureById({
                                    id: v,
                                    groupedPrefectures,
                                  });
                                  return {
                                    label: prefecture?.name || "",
                                    value: prefecture?.id.toString() || "",
                                  };
                                })}
                                onChange={(selectedOptions) => {
                                  onChange(
                                    selectedOptions.map(
                                      (option) => option.value,
                                    ),
                                  );
                                }}
                                menuListContent={
                                  <Box
                                    onClick={() => {
                                      const next = !isAllPrefectureChecked;
                                      setIsAllPrefectureChecked(next);
                                      onCheckAllPrefecture({
                                        checked: next,
                                        onChange,
                                        value,
                                        groupedPrefectures,
                                      });
                                    }}
                                    w="fit-content"
                                  >
                                    <Checkbox
                                      pointerEvents="none"
                                      isChecked={isAllPrefectureChecked}
                                      isDisabled={disabled}
                                    >
                                      全国
                                    </Checkbox>
                                  </Box>
                                }
                                {...field}
                              />
                              {error && (
                                <InputError>{error?.message}</InputError>
                              )}
                            </Box>
                          )}
                        />
                      </InfoItemRight>
                    </InfoItem>
                    <InfoItem>
                      <InfoItemLeft>
                        <FormLabel required>設計実績</FormLabel>
                      </InfoItemLeft>
                      <InfoItemRight>
                        <Box>
                          <Stack gap={3}>
                            {fields.map((field, index) => (
                              <Box key={field.id}>
                                <DesigendArchitecturalFormForEdit
                                  title={`実績 ${index + 1}`}
                                  methods={methods}
                                  field={field}
                                  namePrefix={`contractor_info_attributes.architectural_design_works_attributes.${index}`}
                                  buildingTypes={buildingTypes}
                                  structureTypes={structureTypes}
                                  prefectures={prefectures}
                                  defaultImages={designedArchitecturalWorksImages[
                                    index
                                  ].map((image) => ({
                                    url: image.url,
                                    signed_id: image.signedId,
                                  }))}
                                  onChange={(files) => {
                                    setDesignedArchitecturalWorksImages(
                                      (prev) => {
                                        setIsDirtyDesignedArchitecturalWorksImages(
                                          true,
                                        );
                                        const result = [...prev];
                                        result[index] = files.map((file) =>
                                          createUnifiedFile(file),
                                        );
                                        return result;
                                      },
                                    );
                                  }}
                                  onClickRemove={
                                    fields.length > 1
                                      ? () => {
                                          remove(index);
                                          setDesignedArchitecturalWorksImages(
                                            (prev) => {
                                              setIsDirtyDesignedArchitecturalWorksImages(
                                                true,
                                              );
                                              const result = [...prev];
                                              result.splice(index, 1);
                                              return result;
                                            },
                                          );
                                        }
                                      : undefined
                                  }
                                />
                              </Box>
                            ))}
                          </Stack>
                          <Box ref={architecturalDesignWorksRef}>
                            {architecturalDesignWorksAttributesError !== "" && (
                              <Box mt={2} tabIndex={-1}>
                                <InputError>
                                  {architecturalDesignWorksAttributesError}
                                </InputError>
                              </Box>
                            )}
                          </Box>
                        </Box>
                        {fields.length < 3 && !methods.formState.disabled && (
                          <Button
                            mt={3}
                            py={1}
                            pl={1}
                            pr={2}
                            fontSize="xs"
                            iconSpacing={0.5}
                            variant="outline"
                            leftIcon={<AddIcon boxSize={4} />}
                            onClick={() => {
                              append({
                                project_name: "",
                                building_type_id: "",
                                structure_type_ids: [],
                                project_detail: "",
                                prefecture_id: "",
                                above_ground_floors: "",
                                underground_floors: "",
                                total_floor_area: "",
                                completion_year: "",
                                published: false,
                              });
                              setDesignedArchitecturalWorksImages((prev) => [
                                ...prev,
                                [],
                              ]);
                            }}
                          >
                            <Box pt="1px">実績を追加</Box>
                          </Button>
                        )}
                        <Box fontSize="xs" color="textGray" mt={2}>
                          設計実績は最大3件まで公開することができます。
                        </Box>
                      </InfoItemRight>
                    </InfoItem>
                  </Grid>
                </Box>
              )}
              {(useCase === "client" || useCase === "both") && (
                <Box as="section" mt={14}>
                  <SectionTitle>発注したい業務</SectionTitle>
                  <Grid mt={8} {...gridProps}>
                    <InfoItem>
                      <InfoItemLeft>
                        <FormLabel required>発注したい業種</FormLabel>
                      </InfoItemLeft>
                      <InfoItemRight>
                        <Controller
                          control={control}
                          name="client_info_attributes.desired_industry_ids"
                          render={({
                            field: { disabled, value, onChange, ...field },
                            fieldState: { error },
                          }) => (
                            <Box>
                              <MultiSelectForOptions
                                options={industries}
                                value={value}
                                onChange={onChange}
                                isDisabled={disabled}
                                {...field}
                              />
                              {error && (
                                <InputError>{error?.message}</InputError>
                              )}
                            </Box>
                          )}
                        />
                      </InfoItemRight>
                    </InfoItem>

                    <InfoItem>
                      <InfoItemLeft>
                        <FormLabel required>発注したい分野</FormLabel>
                      </InfoItemLeft>
                      <InfoItemRight>
                        <Controller
                          control={control}
                          name="client_info_attributes.desired_business_field_ids"
                          render={({
                            field: { disabled, value, onChange, ...field },
                            fieldState: { error },
                          }) => (
                            <Box>
                              <MultiSelectForOptions
                                options={businessFields}
                                value={value}
                                onChange={onChange}
                                isDisabled={disabled}
                                {...field}
                              />
                              {error && (
                                <InputError>{error?.message}</InputError>
                              )}
                            </Box>
                          )}
                        />
                      </InfoItemRight>
                    </InfoItem>
                    <InfoItem>
                      <InfoItemLeft>
                        <FormLabel required>発注したい業務補足</FormLabel>
                      </InfoItemLeft>
                      <InfoItemRight>
                        <Controller
                          control={control}
                          name="client_info_attributes.desired_business_service_note"
                          render={({ field, fieldState }) => (
                            <ClientInfoDesiredBusinessServiceNoteField
                              field={field}
                              fieldState={fieldState}
                              isNoLabel
                            />
                          )}
                        />
                      </InfoItemRight>
                    </InfoItem>
                  </Grid>
                </Box>
              )}
            </Box>
            <Stack
              alignItems="center"
              justifyContent="center"
              gap={{ base: 5, sm: 4 }}
              w="full"
              p={6}
              mt={4}
            >
              {applicationing ? (
                <CancelInfoApplicationButton
                  businessEntityCode={ownedBusinessEntity.code}
                />
              ) : (
                <Stack>
                  <Button
                    type="submit"
                    isDisabled={
                      (applicationing || isApproved) &&
                      !isDirty &&
                      !isDirtyDesignedArchitecturalWorksImages
                    }
                    isLoading={isSubmitting}
                  >
                    {isRejected ? (
                      <>再審査内容を確認する</>
                    ) : isApproved ? (
                      <>修正内容を確認する</>
                    ) : (
                      <>申請内容を確認する</>
                    )}
                  </Button>
                </Stack>
              )}
            </Stack>
          </Box>
        </OwnedBusinessEntityLayout>
      </Box>
      {location.hash === "#confirm" && (
        <Box>
          <BusinessMatchingHeader currentUser={currentUser} />
          <Background>
            <Container maxW="container.md" mt={{ base: 6, lg: 20 }}>
              <ShadowCard>
                <Confirmation
                  ownedBusinessEntity={ownedBusinessEntity}
                  values={methods.getValues()}
                  prefectures={prefectures}
                  industries={industries}
                  isSubmitting={isFinalFormSubmitting}
                  businessFields={businessFields}
                  architecturalSoftwares={architecturalSoftwares}
                  buildingTypes={buildingTypes}
                  structureTypes={structureTypes}
                  businessTypes={businessTypes}
                  designedArchitecturalWorksImageUrls={designedArchitecturalWorksImages.map(
                    (images) =>
                      images.map((image) => {
                        if (image instanceof File) {
                          return URL.createObjectURL(image);
                        } else {
                          return image.url;
                        }
                      }),
                  )}
                  onSubmit={handleFinalFormSubmit(onSubmit)}
                  onClickFix={() => navigate(-1)}
                  onClickPreview={() =>
                    openPreview({
                      businessEntity: methods.getValues(),
                      designedArchitecturalWorksImages:
                        designedArchitecturalWorksImages.map((files) =>
                          files.map((file) => ({
                            url: file.url,
                            signed_id: file.signedId,
                          })),
                        ),
                    })
                  }
                />
              </ShadowCard>
            </Container>
          </Background>
        </Box>
      )}
      {location.hash !== "#confirm" && !methods.formState.disabled && (
        <Box position="sticky" bottom={0} zIndex={1}>
          <StickyFooter
            onDraft={draft}
            onPreview={() => {
              void openPreview({
                businessEntity: methods.getValues(),
                designedArchitecturalWorksImages:
                  designedArchitecturalWorksImages.map((files) =>
                    files.map((file) => ({
                      url: file.url,
                      signed_id: file.signedId,
                    })),
                  ),
              });
            }}
          />
        </Box>
      )}
      {draftContent && !flipper.disable_draft_feature && (
        <DraftModal onYes={restoreDraft} onNo={deleteDraft} />
      )}
    </Box>
  );
};

const DraftModal = ({
  onYes,
  onNo,
}: {
  onYes: () => void;
  onNo: () => void;
}) => {
  const { isOpen, onClose } = useDisclosure({ defaultIsOpen: true });
  const flipper = useContext(FlipperContext);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered>
      <ModalOverlay />
      <ModalContent mx={2}>
        <ModalHeader fontWeight={"bold"}>
          下書きした事業者情報があります
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text>下書きした事業者情報を表示しますか？</Text>
          {!flipper.delete_draft_feature && (
            <Text>［いいえ］をクリックすると下書き前に戻ります。</Text>
          )}
        </ModalBody>
        <ModalFooter gap={3}>
          {!flipper.delete_draft_feature && (
            <Button color="gray" onClick={onClose}>
              <Box as="span" pt={0.5}>
                いいえ
              </Box>
            </Button>
          )}
          <Button
            color="primary"
            onClick={() => {
              onYes();
              onClose();
            }}
          >
            <Box as="span" pt={0.5}>
              はい
            </Box>
          </Button>
          {flipper.delete_draft_feature && (
            <Button
              color="red"
              onClick={() => {
                onNo();
                onClose();
              }}
            >
              <Box as="span" pt={0.5}>
                下書き削除
              </Box>
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

const CancelInfoApplicationButton = ({
  businessEntityCode,
}: {
  businessEntityCode: string;
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    handleSubmit,
    formState: { isSubmitting },
  } = useForm();
  const request = useRequest();

  const cancelInfoApplication = async (businessEntityCode: string) => {
    const res = await request(
      businessMatchingOwnedBusinessEntityActiveBusinessEntityInfoApplicationPath(
        businessEntityCode,
      ),
      "DELETE",
    );

    if (res.ok) {
      window.location.reload();
    }
  };

  return (
    <>
      <Button onClick={onOpen}>審査依頼を取り下げる</Button>
      <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered>
        <ModalOverlay />
        <ModalContent mx={2}>
          <ModalHeader fontWeight={"bold"}>審査依頼を取り下げる</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text>審査依頼を取り下げますか？</Text>
          </ModalBody>
          <ModalFooter gap={3}>
            <Button color="gray" onClick={onClose} isDisabled={isSubmitting}>
              <Box as="span" pt={0.5}>
                いいえ
              </Box>
            </Button>
            <Button
              color="red"
              onClick={handleSubmit(async () => {
                await cancelInfoApplication(businessEntityCode);
              })}
              isLoading={isSubmitting}
            >
              <Box as="span" pt={0.5}>
                はい
              </Box>
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

const architecturalSoftwereById = ({
  id,
  architecturalSoftwares,
}: {
  id: number | string;
  architecturalSoftwares: SharedArchitecturalSoftwares;
}) => {
  return architecturalSoftwares
    .map(({ items }) => items)
    .flat()
    .find((item) => item.id.toString() === id.toString());
};

const prefectureById = ({
  id,
  groupedPrefectures,
}: {
  id: number | string;
  groupedPrefectures: GroupedPrefectures;
}) => {
  return groupedPrefectures
    .map(({ prefectures }) => prefectures)
    .flat()
    .find((prefecture) => prefecture.id.toString() === id.toString());
};

const softwareCategoryBySoftwareId = ({
  id,
  architecturalSoftwares,
}: {
  id: number | string;
  architecturalSoftwares: SharedArchitecturalSoftwares;
}) => {
  return architecturalSoftwares.find(({ items }) =>
    items.some((item) => item.id.toString() === id.toString()),
  )?.category;
};

export default EditForm;
